import type { CalendarDataProps } from '@eventbrite/timed-entry';
import type { TemplateProps } from '../containers/template';

export enum AccordionSection {
    date = 'date',
    time = 'time',
    tickets = 'tickets',
}

export enum CalendarType {
    singleEventCard = 'singleEventCard',
    compactWithSession = 'compactWithSession',
    compactWithoutSession = 'compactWithoutSession',
    scrollCalendar = 'scrollCalendar',
}

export enum SalesStatus {
    soldOut = 'soldOut',
    onSale = 'onSale',
    notOnSale = 'notOnSale',
}

export enum DiscountType {
    percentage = 'percentage',
    fixed = 'fixed',
}

interface EventDate {
    /**
     * The date of the event
     */
    date: string;
    salesStatus: SalesStatus;
    checkoutEnabled: boolean;
    urgencySignalPresent: boolean;
    discountPresent: boolean;
    /**
     * The unique identifier of the session, present only if interface Is compactWithSession
     */
    sessionId?: string;
    /**
     * The start date and time of the session in ISO 8601 format, present only if interface Is compactWithSession
     */
    sessionStartDateTime?: string;
}

interface UrgencySignal {
    fewTickets: boolean;
    salesEndSoon: boolean;
    new: boolean;
    popular: boolean;
    goingFast: boolean;
}

interface Discount {
    discountType: DiscountType;
    discountValue: number;
}

export interface TimeSlot {
    id: string;
    startDatetime: string;
    endDatetime: string;
    salesStatus: SalesStatus;
    urgencySignal: UrgencySignal;
    discount: Discount;
}

export interface DateApiResponse {
    interface: CalendarType;
    eventDates: EventDate[];
}

export interface ApiResponseData {
    dateSection: DateApiResponse;
    timeSection: TimeSlot[];
}

export interface ParentEventInfo {
    id: string;
    startUTCDate: string;
    endUTCDate: string;
}

export type DateAvailabilityStatus =
    | 'loading'
    | 'available'
    | 'unavailable'
    | 'discount'
    | 'soldOut'
    | 'fewTicketsLeft';

/**
 * Calendar data based on calendar type:
 *
 * `scrollCalendar`: CalendarDataProps<DateAvailabilityStatus>
 * `compactWithSession`, `compactWithoutSession`, `singleEventCard`: EventDate[]
 */
export type CalendarData =
    | CalendarDataProps<DateAvailabilityStatus>
    | EventDate[];

export interface InfiniteCalendarSeriesNamespace {
    /**
     * @description
     * Holds the necessary parent event information from the event store for the infinite scrollable calendar.
     *
     * Why not take event info from event store directly?
     * To show the existing tickets component (such as ConnectedTicketSelectionCompactSizePage, ConnectedReservedTicketSelectionPage) inside the Tickets accordion
     * the plan is to render them as a child routes of the Series Page and use navigateToTicketSelection action
     * with the appropriate changes to decide the path.
     * When the ticket components are rendered inside the Tickets accordion, event store gets updated.
     * This also changes the date range (start and end date) of the infinite calendar as well.
     * To fix this, required information of original event is stored and will be passed to the InfiniteCalendarSeries component.
     */
    parentEventInfo: ParentEventInfo;
    activeSection: AccordionSection | null;
    calendarType: CalendarType | null;
    calendarData: CalendarData | null;
    timeSlots: TimeSlot[] | null;
    isAppRebrandActive: boolean;
    isDateSectionLoading: boolean;
    isTimeSectionLoading: boolean;
    isTicketSectionLoading: boolean;
    shouldShowTimeError: boolean;
    shouldShowTicketError: boolean;
    selectedDate?: string;
    selectedTime?: string;
    /**
     * @description
     * A flag to decide if time section should be shown or not.
     *   1. When compactWithSession calendar is displayed, time section should not be displayed.
     *   2. When singleEventCard calendar is displayed, after choosing the time slot, time section should not be displayed.
     * Default value should be true
     */
    shouldShowTimeSection?: boolean;
    locale?: string;
    timezone?: string;
    templateProps?: TemplateProps;
}

export interface WithInfiniteCalendarSeriesNamespace {
    infiniteCalendarSeries: InfiniteCalendarSeriesNamespace;
}
